import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Badge,
  Divider,
  Card,
  CardBody,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import { FiSearch, FiCheckCircle, FiAlertCircle, FiEdit2, FiPlus } from 'react-icons/fi';
import { useGetTaxAccount, useSetTaxAccount, useNameEnquiry } from '../../../services/payrollService';
import { useGetPlanInfo } from '../../../services/planService';
import { showSuccessAlert, showErrorAlert } from '../../../utils/alert';
import { useAccessControl } from '../../../hooks/useAccessControl';

// Nigerian banks list (for tax account)
const nigerianBanks = [
  { code: "044", name: "Access Bank" },
  { code: "058", name: "GTBank" },
  { code: "033", name: "UBA" },
  { code: "214", name: "First Bank" },
  { code: "011", name: "First Bank" },
  { code: "050", name: "Ecobank" },
  { code: "070", name: "Fidelity Bank" },
  { code: "221", name: "Stanbic IBTC" },
  { code: "068", name: "Standard Chartered" },
  { code: "232", name: "Sterling Bank" },
  { code: "032", name: "Union Bank" },
  { code: "215", name: "Unity Bank" },
  { code: "035", name: "Wema Bank" },
  { code: "057", name: "Zenith Bank" },
  { code: "076", name: "Polaris Bank" },
  { code: "082", name: "Keystone Bank" },
  { code: "090", name: "VFD Microfinance Bank" },
  { code: "100", name: "Suntrust Bank" },
  { code: "101", name: "Providus Bank" },
  { code: "102", name: "Titan Trust Bank" },
  { code: "103", name: "Globus Bank" },
  { code: "104", name: "Parallex Bank" },
  { code: "105", name: "Premium Trust Bank" },
  { code: "106", name: "Optimus Bank" },
  { code: "107", name: "Moniepoint Microfinance Bank" },
  { code: "108", name: "Opay" },
  { code: "109", name: "Kuda Bank" },
  { code: "110", name: "VBank" },
  { code: "111", name: "Mint Microfinance Bank" },
  { code: "112", name: "M42 Microfinance Bank" },
  { code: "113", name: "Carbon" },
  { code: "114", name: "Fairmoney Microfinance Bank" },
  { code: "115", name: "Sparkle Microfinance Bank" },
  { code: "116", name: "Kredi Money Microfinance Bank" },
  { code: "117", name: "9 Payment Service Bank" },
  { code: "118", name: "Airtel Smartcash PSB" },
  { code: "119", name: "MTN Momo PSB" },
  { code: "120", name: "PalmPay" }
];

const TaxAccountManagement = () => {
  const { canManageSettlements } = useAccessControl();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { planInfo } = useGetPlanInfo();
  
  // API hooks
  const { 
    data: taxAccountData, 
    isLoading: taxAccountLoading,
    error: taxAccountError,
    refetch: refetchTaxAccount
  } = useGetTaxAccount();
  
  const setTaxAccountMutation = useSetTaxAccount();
  const nameEnquiryMutation = useNameEnquiry();
  
  // Form state
  const [formData, setFormData] = useState({
    account_number: '',
    bank_code: '',
    bank_name: '',
    account_name: '',
    currency: 'NGN'
  });
  
  // UI state
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [accountVerificationError, setAccountVerificationError] = useState(null);
  const bankDropdownRef = useRef(null);
  
  // Extract tax account data
  const taxAccount = taxAccountData?.data || taxAccountData;
  
  // Filter banks based on search term
  const filteredBanks = nigerianBanks.filter(bank =>
    bank.name?.toLowerCase().includes(bankSearchTerm.toLowerCase()) ||
    bank.code?.toLowerCase().includes(bankSearchTerm.toLowerCase())
  );
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target)) {
        setShowBankDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Auto-trigger name enquiry when both bank_code and account_number are available
  useEffect(() => {
    if (
      formData.bank_code &&
      formData.account_number &&
      formData.account_number.length >= 10
    ) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        triggerNameEnquiry(formData.bank_code, formData.account_number);
      }, 1000); // Wait 1 second after user stops typing
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bank_code, formData.account_number]);
  
  const triggerNameEnquiry = async (bankCode, accountNumber) => {
    if (!bankCode || !accountNumber || accountNumber.length < 10) {
      return;
    }
    
    setIsVerifyingAccount(true);
    setAccountVerificationError(null);
    
    try {
      const result = await nameEnquiryMutation.mutateAsync({
        account_number: accountNumber,
        bank_code: bankCode
      });
      
      if (result.success && result.account_details?.verified) {
        setFormData(prev => ({
          ...prev,
          account_name: result.account_details.account_name || "",
          bank_name: result.account_details.bank_name || prev.bank_name
        }));
        setAccountVerificationError(null);
      } else {
        setAccountVerificationError(result.message || "Account verification failed");
        setFormData(prev => ({
          ...prev,
          account_name: ""
        }));
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to verify account. Please check the account number and try again.";
      setAccountVerificationError(errorMessage);
      setFormData(prev => ({
        ...prev,
        account_name: ""
      }));
    } finally {
      setIsVerifyingAccount(false);
    }
  };
  
  const handleBankSelect = (bank) => {
    setFormData(prev => ({
      ...prev,
      bank_code: bank.code,
      bank_name: bank.name,
      account_name: "" // Clear account name when bank changes
    }));
    setBankSearchTerm(`${bank.name} (${bank.code})`);
    setShowBankDropdown(false);
    setAccountVerificationError(null);
    
    // Trigger name enquiry if account number is already filled
    if (formData.account_number && formData.account_number.length >= 10) {
      triggerNameEnquiry(bank.code, formData.account_number);
    }
  };
  
  const handleOpenModal = () => {
    if (taxAccount) {
      // Edit mode - populate form with existing data
      setFormData({
        account_number: taxAccount.account_number || '',
        bank_code: taxAccount.bank_code || '',
        bank_name: taxAccount.bank_name || '',
        account_name: taxAccount.account_name || '',
        currency: taxAccount.currency || 'NGN'
      });
      if (taxAccount.bank_name && taxAccount.bank_code) {
        setBankSearchTerm(`${taxAccount.bank_name} (${taxAccount.bank_code})`);
      } else {
        setBankSearchTerm('');
      }
    } else {
      // Create mode - reset form
      setFormData({
        account_number: '',
        bank_code: '',
        bank_name: '',
        account_name: '',
        currency: 'NGN'
      });
      setBankSearchTerm('');
    }
    setAccountVerificationError(null);
    setIsVerifyingAccount(false);
    onOpen();
  };
  
  const handleSaveTaxAccount = async () => {
    if (!formData.account_number || !formData.bank_code) {
      showErrorAlert("Please provide account number and bank");
      return;
    }
    
    if (!formData.account_name) {
      showErrorAlert("Please verify the account by entering a valid account number");
      return;
    }
    
    try {
      await setTaxAccountMutation.mutateAsync({
        account_number: formData.account_number,
        bank_code: formData.bank_code,
        bank_name: formData.bank_name,
        account_name: formData.account_name,
        currency: formData.currency
      });
      
      showSuccessAlert(taxAccount ? "Tax account updated successfully!" : "Tax account configured successfully!");
      onClose();
      refetchTaxAccount();
    } catch (error) {
      console.error("Error saving tax account:", error);
      showErrorAlert("Failed to save tax account. Please try again.");
    }
  };
  
  if (taxAccountLoading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="200px" borderRadius="lg" />
      </VStack>
    );
  }
  
  return (
    <VStack spacing={6} align="stretch">
      {/* Current Tax Account Display */}
      {taxAccount ? (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Heading size="md" color="gray.800">
                  Current Tax Account
                </Heading>
                <Badge colorScheme="green" variant="subtle">
                  <HStack spacing={1}>
                    <FiCheckCircle />
                    <Text>Configured</Text>
                  </HStack>
                </Badge>
              </HStack>
              
              <Divider />
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Account Name
                  </Text>
                  <Text fontSize="lg" fontWeight="600" color="gray.800">
                    {taxAccount.account_name}
                  </Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Account Number
                  </Text>
                  <Text fontSize="lg" fontWeight="600" color="gray.800">
                    {taxAccount.account_number}
                  </Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Bank Name
                  </Text>
                  <Text fontSize="lg" fontWeight="600" color="gray.800">
                    {taxAccount.bank_name}
                  </Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Currency
                  </Text>
                  <Text fontSize="lg" fontWeight="600" color="gray.800">
                    {taxAccount.currency}
                  </Text>
                </Box>
              </SimpleGrid>
              
              {canManageSettlements() && (
                <Button
                  leftIcon={<FiEdit2 />}
                  colorScheme="blue"
                  onClick={handleOpenModal}
                  mt={2}
                >
                  Update Tax Account
                </Button>
              )}
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Tax Account Not Configured</AlertTitle>
            <AlertDescription>
              Configure a tax account to enable automatic settlement payments for tax deductions.
              This account will receive all tax payments from completed payroll batches.
            </AlertDescription>
          </Box>
          {canManageSettlements() && (
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={handleOpenModal}
              ml={4}
            >
              Configure Tax Account
            </Button>
          )}
        </Alert>
      )}
      
      {/* Information Alert */}
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>About Tax Account</AlertTitle>
          <AlertDescription>
            The tax account is used to automatically receive tax payments from payroll batches. 
            All tax deductions collected from employees will be settled to this account after batch completion.
            The account will be verified using bank name enquiry before saving.
          </AlertDescription>
        </Box>
      </Alert>
      
      {/* Configure/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {taxAccount ? 'Update Tax Account' : 'Configure Tax Account'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Account Number</FormLabel>
                <InputGroup>
                  <Input
                    value={formData.account_number}
                    onChange={(e) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        account_number: e.target.value,
                        account_name: ""
                      }));
                      setAccountVerificationError(null);
                    }}
                    placeholder="Enter account number"
                    isDisabled={isVerifyingAccount}
                  />
                  {isVerifyingAccount && (
                    <InputRightElement>
                      <Spinner size="sm" color="blue.500" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {accountVerificationError && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {accountVerificationError}
                  </Text>
                )}
              </FormControl>
              
              <FormControl>
                <FormLabel>Bank</FormLabel>
                <Box position="relative" ref={bankDropdownRef}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      value={bankSearchTerm}
                      onChange={(e) => {
                        setBankSearchTerm(e.target.value);
                        setShowBankDropdown(true);
                      }}
                      onFocus={() => setShowBankDropdown(true)}
                      placeholder="Search banks by name or code..."
                    />
                  </InputGroup>
                  
                  {showBankDropdown && filteredBanks.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      zIndex={1000}
                      maxHeight="200px"
                      overflowY="auto"
                    >
                      {filteredBanks.slice(0, 15).map((bank) => (
                        <Box
                          key={bank.code}
                          p={3}
                          cursor="pointer"
                          _hover={{ bg: "gray.50" }}
                          onClick={() => handleBankSelect(bank)}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          <Text fontWeight="medium">{bank.name}</Text>
                          <Text fontSize="sm" color="gray.600">
                            Code: {bank.code}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </FormControl>
              
              <FormControl>
                <FormLabel>Account Name</FormLabel>
                <Input
                  value={formData.account_name}
                  readOnly
                  isDisabled
                  placeholder="Account name will be verified automatically"
                  bg="gray.50"
                  _disabled={{
                    opacity: 1,
                    cursor: "not-allowed"
                  }}
                />
                {formData.account_name && (
                  <Text fontSize="xs" color="green.600" mt={1}>
                    ✓ Account verified successfully
                  </Text>
                )}
              </FormControl>
              
              <FormControl>
                <FormLabel>Currency</FormLabel>
                <Input
                  value={formData.currency}
                  readOnly
                  bg="gray.50"
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Currency is set to NGN for Nigerian banks
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveTaxAccount}
              isLoading={setTaxAccountMutation.isPending}
              isDisabled={!formData.account_name}
            >
              {taxAccount ? 'Update' : 'Configure'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default TaxAccountManagement;

